import React from 'react'
import Layout from 'components/app/layout'
import Image from 'next/legacy/image'
import Link from 'next/link'
import {CourseJsonLd} from '@skillrecordings/next-seo'
import {Icon} from '@skillrecordings/skill-lesson/icons'
import {isBrowser} from 'utils/is-browser'
import {track} from '@skillrecordings/skill-lesson/utils/analytics'
import {Lesson} from '@skillrecordings/skill-lesson/schemas/lesson'
import {trpc} from 'trpc/trpc.client'
import {type Module} from '@skillrecordings/skill-lesson/schemas/module'
import {capitalize, first} from 'lodash'
import {Section} from '@skillrecordings/skill-lesson/schemas/section'
import cx from 'classnames'
import * as Collection from '@skillrecordings/skill-lesson/video/collection'
import Balancer from 'react-wrap-balancer'
import Testimonials from 'testimonials'
import {MDXRemoteSerializeResult} from 'next-mdx-remote'
import MDX from '@skillrecordings/skill-lesson/markdown/mdx'
import {Skeleton} from '@skillrecordings/ui'
import ResourceContributor from 'components/resource-contributor'
import {CogIcon} from '@heroicons/react/solid'
import {useCoupon} from '@skillrecordings/skill-lesson/path-to-purchase/use-coupon'
import {PriceCheckProvider} from '@skillrecordings/skill-lesson/path-to-purchase/pricing-check-context'
import {Pricing} from '@skillrecordings/skill-lesson/path-to-purchase/pricing'
import {SanityProduct} from '@skillrecordings/commerce-server/dist/@types'
import {useRouter} from 'next/router'

const TutorialTemplate: React.FC<{
  tutorial: Module
  tutorialBodySerialized: MDXRemoteSerializeResult
}> = ({tutorial, tutorialBodySerialized}) => {
  const {title, ogImage, description, testimonials} = tutorial
  const pageTitle = `${title} Tutorial`
  const {data: moduleProgress, status: moduleProgressStatus} =
    trpc.moduleProgress.bySlug.useQuery({
      slug: tutorial.slug.current,
    })

  const product = (tutorial as any)?.product as SanityProduct | undefined
  const router = useRouter()
  const {data: commerceProps} = trpc.pricing.propsForCommerce.useQuery({
    ...(product?.productId && {productId: product.productId}),
    ...(router.query.code && {code: router.query.code as string}),
  })
  const {validCoupon} = useCoupon(commerceProps?.couponFromCode)
  const couponId =
    commerceProps?.couponIdFromCoupon ||
    (validCoupon ? commerceProps?.couponFromCode?.id : undefined)
  const purchases = commerceProps?.purchases || []
  const purchasedProductIds = purchases.map((purchase) => purchase.productId)
  const upgradableTo = product?.upgradableTo
  const pricingProduct = (upgradableTo || product) as SanityProduct | undefined
  const ALLOW_PURCHASE =
    router.query.allowPurchase === 'true' || product?.state === 'active'
  const hasPurchased =
    pricingProduct && purchasedProductIds.includes(pricingProduct.productId)

  return (
    <Layout
      className="mx-auto w-full max-w-screen-lg pt-10 sm:pt-20 lg:pb-24"
      meta={{
        title: pageTitle,
        description,
        ogImage: {
          url: ogImage,
          alt: pageTitle,
        },
      }}
    >
      <CourseMeta title={pageTitle} description={description} />
      {tutorial.state === 'draft' && (
        <div className="sm:px-3">
          <div className="mt-2 flex w-full items-center justify-center gap-2 bg-orange-500/10 px-5 py-3 text-sm leading-tight text-amber-600 dark:bg-orange-400/10 dark:text-orange-300 sm:mt-0 sm:rounded sm:text-base">
            <CogIcon className="h-4 w-4" /> {capitalize(tutorial.moduleType)}{' '}
            under development — you're viewing a draft version.
          </div>
        </div>
      )}
      <main className="z-10 flex flex-col gap-5 lg:flex-row">
        <div className="w-full flex-grow px-5">
          <Header tutorial={tutorial} />
          <article className="prose prose-lg w-full max-w-none dark:prose-invert lg:max-w-xl">
            <MDX contents={tutorialBodySerialized} />
          </article>
          {testimonials && testimonials?.length > 0 && (
            <Testimonials testimonials={testimonials} />
          )}
        </div>
        <aside
          className="relative right-0 w-full px-5 lg:max-w-sm lg:px-0"
          data-workshop=""
        >
          {tutorial.image && !product && (
            <div className="mb-10 flex flex-shrink-0 items-center justify-center md:mb-0 lg:-mr-5">
              <Image
                priority
                src={tutorial.image}
                alt={tutorial.title}
                width={360}
                height={360}
                quality={100}
              />
            </div>
          )}
          {pricingProduct && ALLOW_PURCHASE && !hasPurchased && (
            <div className="mb-8">
              <PriceCheckProvider purchasedProductIds={purchasedProductIds}>
                <div data-pricing-container="" id="buy">
                  <Pricing
                    id="workshop-pricing"
                    allowPurchase={ALLOW_PURCHASE}
                    userId={commerceProps?.userId}
                    product={pricingProduct}
                    options={{withImage: true, withGuaranteeBadge: true}}
                    cancelUrl={process.env.NEXT_PUBLIC_URL + router.asPath}
                    purchased={hasPurchased}
                    couponId={couponId}
                    couponFromCode={commerceProps?.couponFromCode}
                  />
                </div>
              </PriceCheckProvider>
            </div>
          )}
          {tutorial && (
            <Collection.Root module={tutorial}>
              <div className="flex w-full items-center justify-between pb-3 md:mt-5">
                {(tutorial.lessons || tutorial.sections) && (
                  <h3 className="text-xl font-bold">Contents</h3>
                )}
                <Collection.Metadata className="font-mono text-xs font-medium uppercase" />
              </div>
              <Collection.Sections>
                {moduleProgressStatus === 'success' ? (
                  <Collection.Section className="border border-transparent shadow-xl shadow-gray-300/20 transition hover:brightness-100 dark:border-white/5 dark:shadow-none dark:hover:brightness-125">
                    <Collection.Lessons>
                      <Collection.Lesson className="group opacity-80 transition before:pl-9 before:text-primary hover:opacity-100 dark:opacity-90 dark:before:text-teal-300 dark:hover:opacity-100 [&>[data-check-icon]]:text-red-500 [&>div>svg]:text-primary [&>div>svg]:opacity-100 dark:[&>div>svg]:text-teal-300" />
                    </Collection.Lessons>
                  </Collection.Section>
                ) : (
                  <Skeleton className="border bg-background py-6" />
                )}
              </Collection.Sections>
              {/* Used if module has either none or single section so they can be styled differently */}
              <Collection.Lessons className="border-t">
                {moduleProgressStatus === 'success' ? (
                  <Collection.Lesson className="group opacity-80 transition before:pl-9 before:text-primary hover:opacity-100 dark:opacity-90 dark:before:text-teal-300 dark:hover:opacity-100 [&>[data-check-icon]]:text-red-500 [&>div>svg]:text-primary [&>div>svg]:opacity-100 dark:[&>div>svg]:text-teal-300" />
                ) : (
                  <Skeleton className="my-2 border bg-background py-5" />
                )}
              </Collection.Lessons>
            </Collection.Root>
          )}
        </aside>
      </main>
    </Layout>
  )
}

export default TutorialTemplate

const Header: React.FC<{tutorial: any}> = ({tutorial}) => {
  const {title, slug, sections, image, github, instructor, product} = tutorial
  const {data: moduleProgress, status: moduleProgressStatus} =
    trpc.moduleProgress.bySlug.useQuery({
      slug: tutorial.slug.current,
    })

  const isModuleInProgress = (moduleProgress?.completedLessonCount || 0) > 0
  const isModuleCompleted = moduleProgress?.moduleCompleted
  const nextSection = moduleProgress?.nextSection
  const nextLesson = moduleProgress?.nextLesson

  const firstSection = first<Section>(sections)
  const firstLesson = first<Lesson>(firstSection?.lessons || tutorial.lessons)

  return (
    <>
      <header className="relative z-10 flex flex-col-reverse items-center justify-between pb-10 pt-8 sm:pb-16 sm:pt-12 md:flex-row">
        <div className="w-full text-center md:text-left">
          {tutorial.image && (
            <div className="flex w-full items-center justify-center lg:hidden lg:items-start lg:justify-start">
              <Image
                src={tutorial.image}
                alt={tutorial.title}
                width={360}
                height={360}
              />
            </div>
          )}
          <div className="flex items-center justify-center gap-2 pb-4 text-center md:justify-start md:text-left">
            <Link
              href="/tutorials"
              className="text-xs font-bold uppercase tracking-wide text-emerald-500 dark:text-emerald-300"
            >
              Free Tutorial
            </Link>
            {product?.slug && (
              <>
                <span className="text-xs text-muted-foreground">·</span>
                <Link
                  href={`/${product.slug}`}
                  className="text-xs font-medium text-muted-foreground transition hover:text-foreground"
                >
                  Part of {product.name}
                </Link>
              </>
            )}
          </div>
          <h1 className="font-text text-center text-3xl font-bold tracking-tight sm:text-4xl md:text-left lg:text-5xl">
            <Balancer>{title}</Balancer>
          </h1>
          <div className="w-full pt-8 text-lg">
            <div className="flex items-center justify-center gap-3 md:justify-start">
              <ResourceContributor
                name={instructor?.name}
                slug={instructor?.slug}
                image={instructor?.picture?.url}
              />
            </div>
            <div className="flex w-full flex-col items-center justify-center gap-3 pt-8 md:flex-row md:justify-start">
              {(tutorial.lessons || tutorial.sections) && (
                <Link
                  href={
                    firstSection && sections
                      ? {
                          pathname: '/tutorials/[module]/[section]/[lesson]',
                          query: {
                            module: slug.current,
                            section: isModuleCompleted
                              ? firstSection.slug
                              : isModuleInProgress
                              ? nextSection?.slug
                              : firstSection.slug,
                            lesson: isModuleCompleted
                              ? firstLesson?.slug
                              : isModuleInProgress
                              ? nextLesson?.slug
                              : firstLesson?.slug,
                          },
                        }
                      : {
                          pathname: '/tutorials/[module]/[lesson]',
                          query: {
                            module: slug.current,
                            lesson: isModuleCompleted
                              ? firstLesson?.slug
                              : isModuleInProgress
                              ? nextLesson?.slug
                              : firstLesson?.slug,
                          },
                        }
                  }
                  className={cx(
                    'relative flex w-full items-center justify-center rounded-md bg-gradient-to-b from-blue-500 to-blue-600 px-5 py-4 text-lg font-semibold text-white transition hover:brightness-110 focus-visible:ring-white md:max-w-[240px]',
                    {
                      'animate-pulse': moduleProgressStatus === 'loading',
                    },
                  )}
                  onClick={() => {
                    track('clicked start learning', {module: slug.current})
                  }}
                >
                  {isModuleInProgress ? 'Continue' : 'Start'} Learning
                  <span className="pl-2" aria-hidden="true">
                    →
                  </span>
                </Link>
              )}
              {github?.repo && (
                <a
                  className="flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 px-5 py-4 font-medium leading-tight transition hover:bg-gray-100 dark:border-gray-800 dark:hover:bg-gray-800 md:w-auto"
                  href={github.repo}
                  onClick={() => {
                    track('clicked github code link', {module: slug.current})
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon name="Github" size="24" />{' '}
                  {tutorial.workshopApp && 'Workshop App &'} Code
                </a>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  )
}

const CourseMeta = ({
  title,
  description,
}: {
  title: string
  description?: string | null | undefined
}) => (
  <CourseJsonLd
    courseName={title}
    description={description || ''}
    provider={{
      name: `${process.env.NEXT_PUBLIC_PARTNER_FIRST_NAME} ${process.env.NEXT_PUBLIC_PARTNER_LAST_NAME}`,
      type: 'Person',
      url: isBrowser() ? document.location.href : process.env.NEXT_PUBLIC_URL,
    }}
  />
)
